import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CircleSlash2, Pencil, Reply, SmilePlus, Trash2 } from "lucide-react";
import { clsx } from "clsx";
import { QUICK_REACTIONS } from "./emoji-data";
import { EmojiPicker } from "./EmojiPicker";

const PICKER_WIDTH = 320; // w-80
const PICKER_HEIGHT = 384; // h-96

interface MessageActionsProps {
  visible: boolean;
  mine: boolean;
  canEdit: boolean;
  /** True when the current viewer has any reaction on the message. */
  viewerReacted: boolean;
  onHoverChange: (hovered: boolean) => void;
  /** Fires when the quick-reaction row or the full picker opens/closes, so the
   *  bubble can lift its whole row above the messages that follow it. */
  onMenuOpenChange?: (open: boolean) => void;
  onReact: (emoji: string) => void;
  onClearReaction: () => void;
  onReply: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

/**
 * Floating hover bar — rendered inline as an absolutely-positioned sibling of
 * the bubble. No portal: we anchor visually with Tailwind classes instead, so
 * the bar can't get stranded off-screen and every mount/unmount is in-flow.
 */
export function MessageActions({
  visible,
  mine,
  canEdit,
  viewerReacted,
  onHoverChange,
  onMenuOpenChange,
  onReact,
  onClearReaction,
  onReply,
  onEdit,
  onDelete,
}: MessageActionsProps) {
  const [quickOpen, setQuickOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerPos, setPickerPos] = useState<{ top: number; left: number } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const pickerPortalRef = useRef<HTMLDivElement>(null);

  // Every message row is its own stacking context, so an open menu has to be
  // announced upwards — the row itself is what gets raised above the next ones.
  useEffect(() => {
    onMenuOpenChange?.(quickOpen || pickerOpen);
  }, [quickOpen, pickerOpen, onMenuOpenChange]);

  useEffect(() => () => onMenuOpenChange?.(false), [onMenuOpenChange]);

  // Fixed position for the portalled picker: below the action bar when there is
  // room, above it when the message sits near the bottom of the window.
  const placePicker = useCallback(() => {
    const rect = rootRef.current?.getBoundingClientRect();
    if (!rect) return;
    const left = mine
      ? Math.max(8, rect.right - PICKER_WIDTH)
      : Math.min(rect.left, window.innerWidth - PICKER_WIDTH - 8);
    const below = rect.bottom + 4;
    const top =
      below + PICKER_HEIGHT + 8 > window.innerHeight
        ? Math.max(8, rect.top - PICKER_HEIGHT - 4)
        : below;
    setPickerPos({ top, left });
  }, [mine]);

  useEffect(() => {
    if (!pickerOpen) { setPickerPos(null); return; }
    placePicker();
  }, [pickerOpen, placePicker]);

  // Reposition picker on scroll/resize so it follows the message it belongs to.
  useEffect(() => {
    if (!pickerOpen) return;
    window.addEventListener("scroll", placePicker, { capture: true, passive: true });
    window.addEventListener("resize", placePicker);
    return () => {
      window.removeEventListener("scroll", placePicker, true);
      window.removeEventListener("resize", placePicker);
    };
  }, [pickerOpen, placePicker]);

  // Both quickOpen and pickerOpen close only on outside click — not on mouse-leave.
  // Portal picker is outside rootRef so we check pickerPortalRef separately.
  useEffect(() => {
    if (!quickOpen && !pickerOpen) return;
    function onOutsideClick(e: MouseEvent) {
      const inRoot = rootRef.current?.contains(e.target as Node);
      const inPicker = pickerPortalRef.current?.contains(e.target as Node);
      if (!inRoot && !inPicker) {
        setQuickOpen(false);
        setPickerOpen(false);
      }
    }
    window.addEventListener("mousedown", onOutsideClick);
    return () => window.removeEventListener("mousedown", onOutsideClick);
  }, [quickOpen, pickerOpen]);

  // Keep the bar visible as long as any sub-menu is open, regardless of
  // whether the cursor is still hovering over the bubble.
  const effectiveVisible = visible || pickerOpen || quickOpen;

  return (
    <div
      ref={rootRef}
      data-gg-actions-root
      onMouseEnter={() => onHoverChange(true)}
      onMouseLeave={() => onHoverChange(false)}
      className={clsx(
        "absolute z-20 transition-opacity duration-100",
        "-top-2",
        mine ? "right-0" : "left-0",
        effectiveVisible ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none",
      )}
    >
      <span className="absolute -top-1 left-0 right-0 h-2" />
      <div className="bg-white border border-gray-200 rounded-full shadow-md flex items-center gap-0.5 px-1 py-0.5">
        {viewerReacted && (
          <button
            type="button"
            onClick={() => {
              onClearReaction();
              setQuickOpen(false);
              setPickerOpen(false);
            }}
            title="Cofnij moją reakcję"
            className="p-1 hover:bg-red-50 rounded-full text-gray-700"
          >
            <CircleSlash2 size={13} />
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            setQuickOpen((v) => !v);
            setPickerOpen(false);
          }}
          title="Reakcja"
          className="p-1 hover:bg-amber-50 rounded-full text-gray-700"
        >
          <SmilePlus size={13} />
        </button>
        <button
          type="button"
          onClick={onReply}
          title="Odpowiedz"
          className="p-1 hover:bg-amber-50 rounded-full text-gray-700"
        >
          <Reply size={13} />
        </button>
        {mine && canEdit && (
          <button
            type="button"
            onClick={onEdit}
            title="Edytuj"
            className="p-1 hover:bg-amber-50 rounded-full text-gray-700"
          >
            <Pencil size={12} />
          </button>
        )}
        {mine && (
          <button
            type="button"
            onClick={onDelete}
            title="Usuń"
            className="p-1 hover:bg-red-50 rounded-full text-red-600"
          >
            <Trash2 size={12} />
          </button>
        )}
      </div>

      {quickOpen && (
        <div
          className={clsx(
            "absolute top-9 z-30 bg-white border border-gray-200 rounded-full shadow px-2 py-1 flex items-center gap-1",
            mine ? "right-0" : "left-0",
          )}
        >
          {QUICK_REACTIONS.map((e) => (
            <button
              key={e}
              type="button"
              onClick={() => {
                onReact(e);
                setQuickOpen(false);
              }}
              className="text-base hover:scale-125 transition-transform"
            >
              {e}
            </button>
          ))}
          <button
            type="button"
            onClick={() => {
              setQuickOpen(false);
              setPickerOpen(true);
            }}
            className="text-[10px] text-gray-500 hover:text-gray-900 ml-1"
          >
            …
          </button>
        </div>
      )}
      {pickerOpen && pickerPos && createPortal(
        <div
          ref={pickerPortalRef}
          style={{ position: "fixed", top: pickerPos.top, left: pickerPos.left, zIndex: 9999 }}
        >
          <EmojiPicker
            onPick={(e) => {
              onReact(e);
              setPickerOpen(false);
            }}
            onClose={() => setPickerOpen(false)}
          />
        </div>,
        document.body,
      )}
    </div>
  );
}
